import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { ResioLogo } from '@/assets/ResioLogo';
import { useNavigation } from '@react-navigation/native';
import { useAppDispatch, useAppSelector } from '@/store';
import { logout } from '@/store/slices/authSlice';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { WebConfirmModal } from '@/components/common/modals';
import { strings } from '@/constants/residents';
import { useUnreadNotificationsCount } from '@/hooks/common/useUnreadNotificationsCount';
import DesktopNotificationsPopover from '../notification/DesktopNotificationsPopover';

type DesktopHeaderProps = {
  title?: string;
  rightContent?: React.ReactNode;
  onNavigateToBulletins?: () => void;
  onNavigateToNotificationSettings?: () => void;
  onNavigateToChat?: () => void;
};

export const DesktopHeader: React.FC<DesktopHeaderProps> = ({ title, rightContent, onNavigateToBulletins, onNavigateToNotificationSettings, onNavigateToChat }) => {
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((s) => s.auth);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const { total: unreadCount } = useUnreadNotificationsCount();
  const [showNotifications, setShowNotifications] = React.useState(false);
  const onNotifications = () => setShowNotifications((v) => !v);
  const onSignOut = () => {
    if (Platform.OS === 'web') {
      setShowConfirm(true);
      return;
    }
    dispatch(logout());
  };

  // count refresh handled by hook (initial load, focus refresh, interval)

  return (
    <View style={styles.container}>
      <WebConfirmModal
        visible={showConfirm}
        title={strings.logout.desktop.title}
        message={strings.logout.desktop.message}
        confirmText={strings.logout.desktop.confirmText}
        cancelText={strings.logout.desktop.cancelText}
        onConfirm={() => {
          setShowConfirm(false);
          dispatch(logout());
        }}
        onCancel={() => setShowConfirm(false)}
      />
      <View style={styles.leftGroup}>
        <ResioLogo width={64} height={64} />
        <Text numberOfLines={1} style={styles.title}>
          RESIO
        </Text>
      </View>
      <View style={styles.right}>
        {rightContent}
        <View>
          <TouchableOpacity accessibilityRole="button" accessibilityLabel="Notifications" onPress={onNotifications} style={styles.iconButton}>
            <RESIDENTS_SVG_COMPONENTS.BELL width={20} height={20} color={RESIDENTS_DESKTOP_COLORS.ICON_INACTIVE} />
            {unreadCount > 0 && (
              <View style={styles.unreadDot} accessibilityLabel="Unread notifications" />
            )}
          </TouchableOpacity>
          {showNotifications && (
            <DesktopNotificationsPopover
              onClose={() => setShowNotifications(false)}
              navigateToAll={() => {
                setShowNotifications(false);
                if (onNavigateToBulletins) onNavigateToBulletins();
                else try { navigation.navigate('Bulletins'); } catch {}
              }}
              navigateToSettings={() => {
                setShowNotifications(false);
                if (onNavigateToNotificationSettings) onNavigateToNotificationSettings();
                else try { navigation.navigate('Account', { section: 'notificationSettings' }); } catch {}
              }}
              navigateToChat={() => {
                setShowNotifications(false);
                if (onNavigateToChat) onNavigateToChat();
                else try { navigation.navigate('Messages'); } catch {}
              }}
            />
          )}
        </View>
        <TouchableOpacity accessibilityRole="button" accessibilityLabel="Sign out" onPress={onSignOut} style={styles.signOutButton}>
          <Text style={styles.signOutText}>Sign out</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    // height: 72,
    paddingHorizontal: 24,
    paddingTop: 16,
    paddingBottom: 16,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.PRIMARY_BACKGROUND,
    borderBottomWidth: 0,
    alignItems: 'center',
    flexDirection: 'row',
    zIndex: 1000,
    elevation: 16,
  },
  leftGroup: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  title: {
    marginLeft: 12,
    color: '#FFFFFF',
    fontSize: 48,
    fontWeight: '700',
  },
  right: {
    marginLeft: 'auto',
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconButton: {
    height: 36,
    width: 36,
    borderRadius: 8,
    marginLeft: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  unreadDot: {
    position: 'absolute',
    top: 2,
    right: 4,
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: '#FF7629',
    borderWidth: 1,
    borderColor: '#FF7629',
  },
  signOutButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    marginLeft: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: RESIDENTS_DESKTOP_COLORS.BUTTON_SECONDARY,
    borderWidth: 1,
    borderColor: '#374151',
  },
  signOutText: {
    color: RESIDENTS_DESKTOP_COLORS.PRIMARY_BACKGROUND,
    fontSize: 14,
    fontWeight: '600',
  },
});


